/**
 * The employee's own time entries for the visible range.
 *
 * `employee_id` is never sent: step 5 of the 02 §11 chain resolves the caller to
 * themselves and an omitted filter *is* the self scope. See `api/paths.ts`.
 *
 * Every row carries **three** badges, because a row is three independent facts and
 * the repository's standing rule is that distinct states are not collapsed — see
 * `../../timer/badges.tsx`. The one that matters most here is the validation state:
 * a `pending_validation` row says "we could not check", at severity info, with the
 * sentence that the time is recorded in full. Nothing on this screen ever suggests
 * that an entry was, or could be, discarded — Task 31's acceptance criterion is that
 * nothing is ever deleted, in any of the three cases.
 *
 * `edit_requested_at` beside `pending_approval` is the difference between an entry
 * that was typed in by hand and one that was re-opened for correction. `openapi.yaml`
 * keeps those apart in the schema; the row keeps them apart on screen.
 *
 * It re-reads when the timer's `version` changes — that is, exactly when a start,
 * pause, resume or stop succeeded — rather than on a poll. A list that polled would
 * be wrong for up to one interval every time somebody pressed stop, which is the
 * moment they are looking at it.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { timeEntriesPath } from '../../api/paths';
import type { TimeEntry, TimeEntryListResponse } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { PartialData } from '../../states/PartialData';
import { Skeleton } from '../../states/Skeleton';
import {
  SourceBadge,
  TimerStateBadge,
  ValidationBadge,
  validationReasonText,
} from '../../timer/badges';
import { formatDurationSeconds } from '../../timer/elapsed';
import { useTimer } from '../../timer/TimerProvider';

/**
 * Rows per page. Named here because the string that tells the employee what the page
 * excludes has to say the same number — see `entries.excludes`, and the commit that
 * pulled three other such numbers out of the catalogue.
 */
const PAGE_LIMIT = 50;

export interface EntryListProps {
  readonly fromIso: string;
  readonly toIso: string;
  readonly timezone: string;
  /**
   * How wide the window is, in days. Passed rather than restated in the string,
   * because "the last 14 days" written in a catalogue is a sentence that lies the
   * moment the screen changes its range — and nothing would notice.
   */
  readonly rangeDays: number;
}

function EntryRow({
  entry,
  timezone,
}: {
  readonly entry: TimeEntry;
  readonly timezone: string;
}): ReactNode {
  const { t, locale } = useI18n();
  const durations = { hour: t('common.hourShort'), minute: t('common.minuteShort') };
  const reason = validationReasonText(entry.task_validation_reason, t);

  return (
    <li className="wp-row" data-testid={`entry-${entry.id}`}>
      <span className="wp-row__main">
        <span className="wp-mono">{entry.achau_task_id ?? '—'}</span>{' '}
        {entry.task_title ?? t('timer.task.unnamed')}
        <span className="wp-small wp-muted">
          {' · '}
          {formatInstant(entry.started_at, locale, timezone)}
          {entry.ended_at === null || entry.ended_at === undefined
            ? ` · ${t('entries.stillOpen')}`
            : ` → ${formatInstant(entry.ended_at, locale, timezone)}`}
        </span>
      </span>

      <span className="wp-row__figure wp-mono">
        {/* A running entry has no final duration yet, and rendering its recorded
            `duration_seconds` as though it were one would understate the time the
            person is putting in right now. The live number is the timer panel's. */}
        {entry.status === 'running'
          ? t('entries.running')
          : formatDurationSeconds(entry.duration_seconds ?? 0, durations)}
      </span>

      <span className="wp-flags">
        <TimerStateBadge status={entry.status} testId={`entry-state-${entry.id}`} />
        <SourceBadge source={entry.source} testId={`entry-source-${entry.id}`} />
        <ValidationBadge
          state={entry.task_validation_state}
          testId={`entry-validation-${entry.id}`}
        />
        {entry.status === 'pending_approval' &&
        entry.edit_requested_at !== null &&
        entry.edit_requested_at !== undefined ? (
          <Badge
            severity="info"
            icon="hourglass"
            label={t('entries.reopened')}
            title={t('entries.reopened.help')}
            testId={`entry-reopened-${entry.id}`}
          />
        ) : null}
      </span>

      {entry.task_validation_state === 'pending_validation' ? (
        <p className="wp-small wp-muted" data-testid={`entry-pending-note-${entry.id}`}>
          {t('timer.validation.pending.body')}
        </p>
      ) : null}

      {entry.task_validation_state === 'validation_failed' ? (
        <p className="wp-small wp-muted" data-testid={`entry-failed-note-${entry.id}`}>
          {t('timer.validation.failed.body')}
          {reason === null ? null : ` (${reason})`}
        </p>
      ) : null}
    </li>
  );
}

export function EntryList({ fromIso, toIso, timezone, rangeDays }: EntryListProps): ReactNode {
  const { t } = useI18n();
  const { version } = useTimer();
  const { status, data, error, reload } = useApiQuery<TimeEntryListResponse>(
    timeEntriesPath({ from: fromIso, to: toIso, limit: PAGE_LIMIT }),
    version,
  );

  return (
    <section className="wp-section" aria-labelledby="entries-heading">
      <h2 className="wp-section__title" id="entries-heading" tabIndex={-1}>
        {t('entries.heading')}
      </h2>

      {status === 'loading' ? (
        <Skeleton rows={3} label={t('entries.loading')} testId="entries-skeleton" />
      ) : null}

      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('entries.subject')}
          testId="entries-error"
        />
      ) : null}

      {/* `has_more` is a real partial view, and the only one this list has: the page
          stops at 50 and the range does not. `PRODUCT.md` §4 asks which part is missing
          and what the shown figure therefore excludes — here, entries earlier in the
          same window. It is deliberately **not** used for `pending_validation`, which
          is a complete record with an unanswered question attached to it. */}
      {data?.page.has_more === true ? (
        <PartialData
          excludes={t('entries.excludes', { days: rangeDays, limit: PAGE_LIMIT })}
          testId="entries-partial"
        />
      ) : null}

      {data === null ? null : data.data.length === 0 ? (
        <EmptyState
          title={t('entries.empty.title')}
          description={t('entries.empty.body', { days: rangeDays })}
          testId="entries-empty"
        />
      ) : (
        <ul className="wp-rows" data-testid="entries-list">
          {data.data.map((entry) => (
            <EntryRow key={entry.id} entry={entry} timezone={timezone} />
          ))}
        </ul>
      )}
    </section>
  );
}
